import React, { useEffect, useRef, useState } from "react";
import GemBadge, { TIERS } from "./GemBadge";
import { Sparkles, Download, Share2, X, Instagram, Music2 } from "lucide-react";
import { toast } from "sonner";

// Cheap, self-contained confetti — no extra deps. Burst once, then idle.
function useConfetti(active, color) {
  const ref = useRef(null);
  useEffect(() => {
    if (!active || !ref.current) return;
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const DPR = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * DPR;
      canvas.height = window.innerHeight * DPR;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(DPR, DPR);
    };
    resize();
    window.addEventListener("resize", resize);

    const palette = [color, "#ffffff", "#FBBF24", "#22D3EE", "#F472B6", "#A78BFA"];
    const N = 180;
    const parts = Array.from({ length: N }, () => ({
      x: window.innerWidth / 2 + (Math.random() - 0.5) * 80,
      y: window.innerHeight / 2 - 60,
      vx: (Math.random() - 0.5) * 10,
      vy: -Math.random() * 14 - 4,
      g: 0.35 + Math.random() * 0.15,
      r: 4 + Math.random() * 6,
      a: 1,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.4,
      c: palette[Math.floor(Math.random() * palette.length)],
      shape: Math.random() > 0.5 ? "rect" : "circle",
    }));

    let raf = 0;
    let t0 = performance.now();
    const tick = (t) => {
      const elapsed = t - t0;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      parts.forEach((p) => {
        p.vy += p.g;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.a = Math.max(0, 1 - elapsed / 3200);
        ctx.globalAlpha = p.a;
        ctx.fillStyle = p.c;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        if (p.shape === "rect") {
          ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r * 1.4);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.r / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      });
      if (elapsed < 3500) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [active, color]);
  return ref;
}

function drawShareCard({ name, tierKey }) {
  const tier = TIERS[tierKey] || TIERS.white;
  const c = document.createElement("canvas");
  // Instagram-friendly portrait (1080x1350)
  c.width = 1080;
  c.height = 1350;
  const ctx = c.getContext("2d");

  // Background
  const grd = ctx.createLinearGradient(0, 0, c.width, c.height);
  grd.addColorStop(0, "#0b0b0f");
  grd.addColorStop(1, tier.color);
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, c.width, c.height);

  // Subtle noise dots
  ctx.fillStyle = "rgba(255,255,255,0.05)";
  for (let i = 0; i < 1200; i++) {
    ctx.fillRect(Math.random() * c.width, Math.random() * c.height, 1.2, 1.2);
  }

  // Top label
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "700 38px Inter, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("HIDDEN GEM", c.width / 2, 130);

  // Sub-label
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "600 28px Inter, system-ui, sans-serif";
  ctx.fillText("REPUTATION TIER UP", c.width / 2, 175);

  // Big Gem (simple stylised gem shape)
  const cx = c.width / 2;
  const cy = 640;
  const size = 320;
  const top = cy - size;
  const left = cx - size * 0.85;
  const right = cx + size * 0.85;
  const bottom = cy + size * 0.95;

  // Glow
  const glow = ctx.createRadialGradient(cx, cy, size * 0.2, cx, cy, size * 1.6);
  glow.addColorStop(0, `${tier.color}cc`);
  glow.addColorStop(1, "transparent");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 200, c.width, 900);

  ctx.beginPath();
  ctx.moveTo(cx, top);
  ctx.lineTo(right, cy - size * 0.3);
  ctx.lineTo(cx + 60, bottom);
  ctx.lineTo(cx - 60, bottom);
  ctx.lineTo(left, cy - size * 0.3);
  ctx.closePath();
  ctx.fillStyle = tier.color;
  ctx.fill();
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 6;
  ctx.stroke();

  // Highlight
  ctx.beginPath();
  ctx.moveTo(cx, top);
  ctx.lineTo(cx + 70, cy - size * 0.3);
  ctx.lineTo(cx - 70, cy - size * 0.3);
  ctx.closePath();
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.fill();

  // Name + tier
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 72px Inter, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(name, c.width / 2, 1080);

  ctx.font = "900 96px Inter, system-ui, sans-serif";
  ctx.fillStyle = tier.color;
  ctx.fillText(tier.name.toUpperCase(), c.width / 2, 1180);

  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.font = "600 36px Inter, system-ui, sans-serif";
  ctx.fillText("hiddengem.app · " + tier.label, c.width / 2, 1240);

  return c;
}

async function canvasToFile(canvas, filename) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) return resolve(null);
      const file = new File([blob], filename, { type: "image/png" });
      resolve({ file, blob });
    }, "image/png", 0.95);
  });
}

export default function TierUpCelebration({ open, tierUp, userName, onClose }) {
  const tierKey = tierUp?.to_tier || "white";
  const meta = TIERS[tierKey] || TIERS.white;
  const confettiRef = useConfetti(open, meta.color);
  const [sharing, setSharing] = useState(false);

  if (!open || !tierUp) return null;

  const handleNativeShare = async () => {
    setSharing(true);
    try {
      const canvas = drawShareCard({ name: userName || "You", tierKey });
      const fileInfo = await canvasToFile(canvas, `hidden-gem-${tierKey}.png`);
      if (!fileInfo) return;
      const text = `I just leveled up to ${meta.name} on Hidden Gem 💎`;
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [fileInfo.file] })) {
        await navigator.share({
          files: [fileInfo.file],
          title: "Hidden Gem · Tier Up",
          text,
        });
      } else if (navigator.share) {
        await navigator.share({ title: "Hidden Gem · Tier Up", text, url: window.location.origin });
      } else {
        const url = URL.createObjectURL(fileInfo.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `hidden-gem-${tierKey}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Image downloaded — share it on Instagram or TikTok!");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Share failed — try Save");
    } finally {
      setSharing(false);
    }
  };

  const handleDownload = async () => {
    const canvas = drawShareCard({ name: userName || "You", tierKey });
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hidden-gem-${tierKey}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Saved! Open Instagram or TikTok and post it as a story.");
    }, "image/png", 0.95);
  };

  const handleInstagram = async () => {
    // Instagram doesn't allow direct sticker-share from the web. Best path:
    // 1) try Web Share with file (mobile IG appears in the sheet)
    // 2) else download + open the Stories camera deep link
    await handleNativeShare();
    setTimeout(() => {
      // Mobile deep-link nudge to open Instagram (Story camera)
      window.open("instagram://story-camera", "_blank");
    }, 700);
  };

  const handleTiktok = async () => {
    await handleNativeShare();
    setTimeout(() => {
      window.open("snssdk1233://", "_blank"); // TikTok scheme — falls back if not installed
    }, 700);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      data-testid="tier-up-celebration"
      style={{ background: `radial-gradient(closest-side, ${meta.color}33, rgba(0,0,0,0.95))` }}
    >
      <canvas
        ref={confettiRef}
        className="fixed inset-0 pointer-events-none"
        data-testid="tier-up-confetti"
      />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white z-10"
        data-testid="tier-up-close"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="relative max-w-md w-[90%] text-center text-white space-y-5 px-4">
        <p className="text-[11px] uppercase tracking-[0.3em] font-black text-zinc-300" data-testid="tier-up-overline">Tier up</p>
        <p className="text-sm text-zinc-300">
          From <span className="font-bold">{tierUp.from_tier_name || "previous tier"}</span>
        </p>

        <div className="relative mx-auto" style={{ width: 200, height: 200 }}>
          <div
            className="absolute inset-0 rounded-full blur-3xl animate-pulse"
            style={{ background: meta.color, opacity: 0.45 }}
          />
          <div className="relative grid place-items-center w-full h-full">
            <div className="scale-[2.6]" style={{ transform: "scale(2.6)" }}>
              <GemBadge tier={tierKey} size="xl" />
            </div>
          </div>
        </div>

        <div className="space-y-1">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter" data-testid="tier-up-name">
            {meta.name}
          </h2>
          <p className="text-base text-zinc-300 inline-flex items-center gap-1.5 justify-center">
            <Sparkles className="w-4 h-4" style={{ color: meta.color }} />
            {meta.label}
          </p>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          <button
            onClick={handleInstagram}
            disabled={sharing}
            className="h-12 rounded-full font-bold text-black inline-flex items-center justify-center gap-2 transition-transform active:scale-95"
            style={{ background: "linear-gradient(90deg,#f59e0b,#ec4899,#8b5cf6)" }}
            data-testid="tier-up-instagram"
          >
            <Instagram className="w-4 h-4" /> Share to Instagram
          </button>
          <button
            onClick={handleTiktok}
            disabled={sharing}
            className="h-12 rounded-full font-bold text-white inline-flex items-center justify-center gap-2 bg-black border border-white/20 hover:bg-white/10 active:scale-95"
            data-testid="tier-up-tiktok"
          >
            <Music2 className="w-4 h-4" /> Share to TikTok
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleNativeShare}
              disabled={sharing}
              className="flex-1 h-11 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-bold inline-flex items-center justify-center gap-2"
              data-testid="tier-up-share"
            >
              <Share2 className="w-3.5 h-3.5" /> More
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 h-11 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-bold inline-flex items-center justify-center gap-2"
              data-testid="tier-up-download"
            >
              <Download className="w-3.5 h-3.5" /> Save image
            </button>
          </div>
        </div>

        <button
          onClick={onClose}
          className="text-xs text-zinc-400 underline-offset-4 hover:underline"
          data-testid="tier-up-dismiss"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
